import React, { useEffect, useRef, useState } from 'react';
import { CheckCircle2, ChevronDown, Circle, Lock, X } from 'lucide-react';
import patterns from '@/shared/ui/styles/patterns.module.css';
import styles from '../pages/AgentsPage.module.css';
import { AGENT_LANGUAGE_OPTIONS, type CallTemplate, type DraftAgent } from '../agentShared';
import type { AgentConfig } from '../api/callsApi';
import { activatableProps } from '../utils/a11y';

// Props every builder section receives from the orchestrator.
export interface SectionProps {
  draft: DraftAgent;
  template: CallTemplate;
  set: (key: keyof DraftAgent, value: unknown) => void;
  setConfig: <K extends keyof AgentConfig>(key: K, value: AgentConfig[K]) => void;
}

// ── SectionCard ───────────────────────────────────────────────────────────────

export function SectionCard({
  icon: Icon,
  title,
  sub,
  done,
  locked,
  open,
  onToggle,
  children,
}: {
  icon: React.ElementType;
  title: string;
  sub?: React.ReactNode;
  done?: boolean;
  locked?: boolean;
  open?: boolean;
  onToggle?: () => void;
  children: React.ReactNode;
}) {
  const collapsible = !!onToggle;
  return (
    <div className={`${patterns.panel} ${styles.sectionCard}`}>
      <div
        className={`${styles.sectionCardHeader} ${collapsible ? styles.sectionCardHeaderClickable : ''}`}
        {...(onToggle ? { ...activatableProps(onToggle), 'aria-expanded': !!open } : {})}
      >
        <div className={styles.sectionCardTitle}>
          {locked ? (
            <Lock size={13} color="var(--text-muted)" />
          ) : done ? (
            <CheckCircle2 size={14} color="var(--success)" />
          ) : (
            <Circle size={14} color="var(--text-muted)" />
          )}
          {title}
          {sub && <div className={styles.sectionCardSub}>{sub}</div>}
        </div>
        {collapsible ? (
          <ChevronDown
            size={14}
            color="var(--text-secondary)"
            className={open ? styles.chevronOpen : styles.chevronClosed}
          />
        ) : (
          <Icon size={13} color="var(--text-secondary)" />
        )}
      </div>
      {(!collapsible || open) && (
        <div className={collapsible ? styles.sectionCardBodyBordered : styles.sectionCardBody}>
          {children}
        </div>
      )}
    </div>
  );
}

// ── Toggle row ────────────────────────────────────────────────────────────────

export function ToggleRow({
  title,
  description,
  on,
  locked,
  lockedOff,
  lockedLabel,
  onToggle,
}: {
  title: string;
  description?: string;
  on?: boolean;
  locked?: boolean;
  lockedOff?: boolean;
  lockedLabel?: string;
  onToggle?: () => void;
}) {
  const isLocked = locked || lockedOff;
  return (
    <div className={styles.toggleRow}>
      <div>
        <div className={styles.toggleTitle}>{title}</div>
        {description && <div className={styles.toggleDesc}>{description}</div>}
      </div>
      <div className={styles.toggleControl}>
        <button
          type="button"
          role="switch"
          aria-checked={isLocked ? !lockedOff : !!on}
          aria-label={title}
          disabled={isLocked}
          className={[
            styles.switch,
            !isLocked && on ? styles.switchOn : '',
            locked ? styles.switchLocked : '',
            lockedOff ? styles.switchLockedOff : '',
          ]
            .filter(Boolean)
            .join(' ')}
          onClick={isLocked ? undefined : onToggle}
        />
        {isLocked && (
          <span className={lockedOff ? styles.lockNoteMuted : styles.lockNote}>
            {lockedLabel ?? (lockedOff ? 'OFF — BY DESIGN' : 'LOCKED ON')}
          </span>
        )}
      </div>
    </div>
  );
}

// ── Generic chip-list input (emails, carriers, procedures…) ───────────────────

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function ChipListInput({
  value,
  onChange,
  placeholder,
  inputType = 'text',
}: {
  value: string[];
  onChange: (v: string[]) => void;
  placeholder?: string;
  inputType?: 'text' | 'email';
}) {
  const [input, setInput] = useState('');
  const [error, setError] = useState<string | null>(null);
  const inputRef = React.useRef<HTMLInputElement>(null);

  function commit(raw: string) {
    const item = raw.trim().replace(/,$/, '').trim();
    // Catch a bad address here rather than as "Validation failed" on save.
    if (item && inputType === 'email' && !EMAIL_RE.test(item)) {
      setError(`"${item}" isn't a valid email address.`);
      return;
    }
    setError(null);
    if (item && !value.includes(item)) {
      onChange([...value, item]);
    }
    setInput('');
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter' || e.key === 'Tab' || e.key === ',') {
      if (input.trim()) {
        e.preventDefault();
        commit(input);
      }
    } else if (e.key === 'Backspace' && input === '' && value.length > 0) {
      onChange(value.slice(0, -1));
    }
  }

  return (
    <div className={styles.emailPillWrap} onClick={() => inputRef.current?.focus()}>
      {value.map((item) => (
        <span key={item} className={styles.emailPill}>
          {item}
          <button
            type="button"
            className={styles.emailPillRemove}
            onClick={(e) => {
              e.stopPropagation();
              onChange(value.filter((v) => v !== item));
            }}
            aria-label={`Remove ${item}`}
          >
            <X size={11} />
          </button>
        </span>
      ))}
      <input
        ref={inputRef}
        type={inputType}
        className={styles.emailPillTextInput}
        value={input}
        placeholder={placeholder}
        onChange={(e) => {
          setInput(e.target.value);
          if (error) setError(null);
        }}
        onKeyDown={handleKeyDown}
        onBlur={() => {
          if (input.trim()) commit(input);
        }}
        aria-invalid={!!error}
      />
      {error && (
        <p className={styles.hintTextWarning} role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

export function EmailPillInput({
  value,
  onChange,
}: {
  value: string[];
  onChange: (v: string[]) => void;
}) {
  return (
    <ChipListInput
      value={value}
      onChange={onChange}
      inputType="email"
      placeholder={value.length === 0 ? 'frontdesk@yourpractice.com' : 'Add another…'}
    />
  );
}

// ── Language toggle (fixed set — English (US) / Spanish (Latin America)) ─────

export function LanguageToggle({
  languages,
  onChange,
}: {
  languages: string[];
  onChange: (langs: string[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const available = AGENT_LANGUAGE_OPTIONS.filter((opt) => !languages.includes(opt.code));

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  return (
    <div className={styles.languagePicker}>
      <div className={styles.languageChips}>
        {AGENT_LANGUAGE_OPTIONS.filter((opt) => languages.includes(opt.code)).map((opt) => (
          <span key={opt.code} className={styles.languageChip}>
            {opt.label}
            {languages.length > 1 && (
              <button
                type="button"
                className={styles.languageChipRemove}
                onClick={() => onChange(languages.filter((l) => l !== opt.code))}
                aria-label={`Remove ${opt.label}`}
              >
                <X size={10} />
              </button>
            )}
          </span>
        ))}
        {available.length > 0 && (
          <div className={styles.languageAddWrap} ref={wrapRef}>
            <button
              type="button"
              className={styles.languageAddBox}
              onClick={() => setOpen((v) => !v)}
              aria-expanded={open}
            >
              + Add language
            </button>
            {open && (
              <div className={styles.languageDropdown}>
                {available.map((opt) => (
                  <button
                    key={opt.code}
                    type="button"
                    className={styles.languageDropdownOption}
                    onClick={() => {
                      onChange([...languages, opt.code]);
                      setOpen(false);
                    }}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
      <p className={styles.hintText}>
        The agent detects the caller's language and responds in it automatically.
      </p>
    </div>
  );
}
